import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { EquipmentItem, EquipmentListResponse } from '../types';

interface Org {
  id: string;
  name: string;
  operator: string | null;
  state: string;
  mine_type: string;
  subscription_tier: string;
  status: string;
}

const STATUS_COLORS: Record<string, string> = {
  in_service: '#4caf50',
  in_stores: '#ff9800',
  under_repair: '#f44336',
  under_overhaul: '#9c27b0',
  decommissioned: '#757575',
  condemned: '#d32f2f',
};

const PSF_COLORS: Record<string, string> = {
  complete: '#4caf50',
  incomplete: '#ff9800',
  under_review: '#2196f3',
  not_started: '#bdbdbd',
};

const Dashboard: React.FC = () => {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  const [equipment, setEquipment] = useState<EquipmentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [exOnly, setExOnly] = useState(false);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('psf_user') || '{}');

  // Load orgs on mount
  useEffect(() => {
    const loadOrgs = async () => {
      try {
        const { data } = await api.get<Org[]>('/api/organisations/');
        setOrgs(data);
        if (data.length > 0) {
          const saved = localStorage.getItem('psf_selected_org');
          const orgId = saved && data.find(o => o.id === saved) ? saved : data[0].id;
          setSelectedOrg(orgId);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadOrgs();
  }, []);

  const fetchEquipment = useCallback(async () => {
    if (!selectedOrg) { setLoading(false); return; }
    setLoading(true);
    try {
      const params: any = { org_id: selectedOrg, page_size: 50 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (exOnly) params.ex_only = true;

      const { data } = await api.get<EquipmentListResponse>('/api/equipment/', { params });
      setEquipment(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, search, statusFilter, exOnly]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const handleOrgChange = (orgId: string) => {
    setSelectedOrg(orgId);
    localStorage.setItem('psf_selected_org', orgId);
  };

  const handleLogout = () => {
    localStorage.removeItem('psf_token');
    localStorage.removeItem('psf_user');
    navigate('/login');
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isOverdue = (d: string | null) => {
    if (!d) return false;
    return new Date(d) < new Date();
  };

  const currentOrg = orgs.find(o => o.id === selectedOrg);

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <img src="/logo-white.png" alt="JMB Engineering" style={{ height: '38px' }} />
          <div style={{ width: '1px', height: '36px', background: 'rgba(255,255,255,0.2)' }} />
          <div>
            <h1 style={styles.headerTitle}>PSF Platform</h1>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '1px', letterSpacing: '0.5px' }}>Plant Safety File Platform</div>
          </div>
        </div>
        <div style={styles.headerRight}>
          {orgs.length > 1 && (
            <select
              value={selectedOrg}
              onChange={(e) => handleOrgChange(e.target.value)}
              style={styles.orgSelect}
            >
              {orgs.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          )}
          {orgs.length === 1 && <span style={styles.orgName}>{currentOrg?.name}</span>}
          <span style={styles.userName}>{user.name}</span>
          <span style={styles.userRole}>{user.role?.replace(/_/g, ' ')}</span>
          <button onClick={() => navigate('/chat')} style={styles.logoutBtn}>🤖 AI Chat</button>
          <button onClick={() => navigate('/its')} style={styles.logoutBtn}>📋 ITS</button>
          <button onClick={() => navigate('/alerts')} style={styles.logoutBtn}>🔔 Alerts</button>
          {(user.role === 'jmb_admin' || user.role === 'eem' || user.role === 'site_admin') && (
            <button onClick={() => navigate('/admin')} style={styles.logoutBtn}>⚙ Admin</button>
          )}
          <button onClick={handleLogout} style={styles.logoutBtn}>Logout</button>
        </div>
      </header>

      {/* MDA Alert Banner */}
      {equipment.reduce((acc, e) => acc + e.mda_count, 0) > 0 && (
        <div style={styles.alertBanner}>
          <span style={{ fontSize: '18px' }}>⚠️</span>
          <div>
            <strong>{equipment.reduce((acc, e) => acc + e.mda_count, 0)} MDA items</strong> requiring removal by{' '}
            <strong>September 2028</strong>
            <span style={{ opacity: 0.8, marginLeft: '8px' }}>
              ({Math.ceil((new Date('2028-09-01').getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days remaining)
            </span>
          </div>
        </div>
      )}

      {/* Overdue Alert */}
      {equipment.filter(e => e.next_overhaul_due && isOverdue(e.next_overhaul_due)).length > 0 && (
        <div style={{ ...styles.alertBanner, background: '#fff3e0', borderColor: '#ff9800' }}>
          <span style={{ fontSize: '18px' }}>🔧</span>
          <div>
            <strong style={{ color: '#e65100' }}>
              {equipment.filter(e => e.next_overhaul_due && isOverdue(e.next_overhaul_due)).length} overhaul(s) overdue
            </strong>
            {' — '}
            {equipment.filter(e => e.next_overhaul_due && isOverdue(e.next_overhaul_due))
              .map(e => e.equipment_tag).join(', ')}
          </div>
        </div>
      )}

      {/* Stats Bar */}
      <div style={styles.statsBar}>
        <div style={styles.stat}>
          <div style={styles.statValue}>{total}</div>
          <div style={styles.statLabel}>Total Equipment</div>
        </div>
        <div style={styles.stat}>
          <div style={{ ...styles.statValue, color: '#4caf50' }}>
            {equipment.filter(e => e.explosion_protected).length}
          </div>
          <div style={styles.statLabel}>Ex Protected</div>
        </div>
        <div style={styles.stat}>
          <div style={{ ...styles.statValue, color: '#f44336' }}>
            {equipment.reduce((acc, e) => acc + e.mda_count, 0)}
          </div>
          <div style={styles.statLabel}>MDA Items</div>
        </div>
        <div style={styles.stat}>
          <div style={{ ...styles.statValue, color: '#ff9800' }}>
            {equipment.filter(e => e.next_overhaul_due && isOverdue(e.next_overhaul_due)).length}
          </div>
          <div style={styles.statLabel}>Overhauls Overdue</div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <input
          type="text"
          placeholder="Search equipment tag, description, serial, manufacturer..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={styles.select}>
          <option value="">All Statuses</option>
          <option value="in_service">In Service</option>
          <option value="in_stores">In Stores</option>
          <option value="under_repair">Under Repair</option>
          <option value="under_overhaul">Under Overhaul</option>
          <option value="decommissioned">Decommissioned</option>
        </select>
        <label style={styles.checkboxLabel}>
          <input type="checkbox" checked={exOnly} onChange={(e) => setExOnly(e.target.checked)} />
          Ex Only
        </label>
      </div>

      {/* Equipment Table */}
      <div style={styles.tableContainer} id="eq-table-wrap">
        {loading ? (
          <div style={styles.loading}>Loading equipment...</div>
        ) : equipment.length === 0 ? (
          <div style={styles.empty}>No equipment found. Add your first piece of plant to get started.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Tag</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Manufacturer</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Ex</th>
                <th style={styles.th}>Components</th>
                <th style={styles.th}>PSF</th>
                <th style={styles.th}>Next Overhaul</th>
              </tr>
            </thead>
            <tbody>
              {equipment.map((eq) => (
                <tr key={eq.id} style={styles.tr} onClick={() => navigate(`/equipment/${eq.id}`)}>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{eq.equipment_tag}</td>
                  <td style={styles.td}>{eq.description || '—'}</td>
                  <td style={styles.td}>
                    <span style={styles.typeTag}>{eq.equipment_type.replace(/_/g, ' ')}</span>
                  </td>
                  <td style={styles.td}>{eq.manufacturer || '—'}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, background: STATUS_COLORS[eq.status] || '#999' }}>
                      {eq.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {eq.explosion_protected ? <span style={styles.exBadge}>⚡ Ex</span> : '—'}
                  </td>
                  <td style={styles.td}>
                    <span>{eq.component_count}</span>
                    {eq.mda_count > 0 && <span style={styles.mdaBadge}>{eq.mda_count} MDA</span>}
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.psfBadge, background: PSF_COLORS[eq.psf_status] || '#999' }}>
                      {eq.psf_status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      color: isOverdue(eq.next_overhaul_due) ? '#d32f2f' : '#333',
                      fontWeight: isOverdue(eq.next_overhaul_due) ? 600 : 400,
                    }}>
                      {formatDate(eq.next_overhaul_due)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <footer style={styles.pageFooter}>
        © 2026 JMB Engineering Pty Ltd — Plant Safety File Platform
      </footer>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', background: '#f5f6fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', display: 'flex', flexDirection: 'column' },
  pageFooter: { marginTop: 'auto', padding: '16px 32px', background: '#1a1a2e', color: 'rgba(255,255,255,0.4)', fontSize: '12px', textAlign: 'center' as const, letterSpacing: '0.5px' },
  alertBanner: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 32px', background: '#ffebee', borderLeft: '4px solid #f44336', fontSize: '14px', color: '#333' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px', background: '#1a1a2e', color: '#fff' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerTitle: { margin: 0, fontSize: '20px', fontWeight: 600 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  orgSelect: { padding: '6px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '13px' },
  orgName: { fontWeight: 600, fontSize: '14px', opacity: 0.9 },
  userName: { fontWeight: 600, fontSize: '14px' },
  userRole: { fontSize: '12px', opacity: 0.7, textTransform: 'capitalize' as const },
  logoutBtn: { padding: '6px 16px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  statsBar: { display: 'flex', gap: '24px', padding: '24px 32px', background: '#fff', borderBottom: '1px solid #e0e0e0' },
  stat: { flex: 1, textAlign: 'center' as const },
  statValue: { fontSize: '28px', fontWeight: 700, color: '#1a1a2e' },
  statLabel: { fontSize: '12px', color: '#888', marginTop: '4px', textTransform: 'uppercase' as const },
  filters: { display: 'flex', gap: '12px', padding: '16px 32px', alignItems: 'center' },
  searchInput: { flex: 1, padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', outline: 'none' },
  select: { padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', background: '#fff' },
  checkboxLabel: { fontSize: '14px', display: 'flex', gap: '6px', alignItems: 'center' },
  tableContainer: { padding: '0 32px 32px' },
  table: { width: '100%', borderCollapse: 'collapse' as const, background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  th: { padding: '12px 16px', textAlign: 'left' as const, fontSize: '12px', fontWeight: 600, color: '#666', textTransform: 'uppercase' as const, borderBottom: '2px solid #e0e0e0', background: '#fafafa' },
  tr: { cursor: 'pointer', transition: 'background 0.15s', borderBottom: '1px solid #f0f0f0' },
  td: { padding: '14px 16px', fontSize: '14px', color: '#333' },
  statusBadge: { padding: '3px 10px', borderRadius: '12px', color: '#fff', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize' as const, whiteSpace: 'nowrap' as const },
  psfBadge: { padding: '3px 10px', borderRadius: '12px', color: '#fff', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize' as const },
  exBadge: { padding: '3px 8px', borderRadius: '4px', background: '#fff3e0', color: '#e65100', fontSize: '11px', fontWeight: 600 },
  mdaBadge: { marginLeft: '8px', padding: '2px 6px', borderRadius: '4px', background: '#ffebee', color: '#c62828', fontSize: '10px', fontWeight: 600 },
  typeTag: { fontSize: '12px', color: '#555', textTransform: 'capitalize' as const },
  loading: { padding: '48px', textAlign: 'center' as const, color: '#999' },
  empty: { padding: '48px', textAlign: 'center' as const, color: '#999' },
};

export default Dashboard;
